import PropTypes from 'prop-types';
import React, { Component } from 'react';
import styled from 'styled-components';
import { injectIntl, FormattedMessage } from 'react-intl';
import { navy, grey25, grey75 } from '@zocdoc/branding/colors';

import {
    NON_INPUT_X_AFFORDANCE_DESKTOP,
    NON_INPUT_X_AFFORDANCE_MOBILE,
} from '../../../../picker-constants';

import { FontFamily } from '../../../../typography';
import { breakpoint } from '../../../../grid';
import { TAB, CARRIER } from '../../../constants';
import { Check } from '../../../../icons';

class BaseHeaderView extends Component {
    static propTypes = {
        carrier: PropTypes.shape({
            id: PropTypes.string,
        }),
    };

    bcbsStepRef = React.createRef()
    stepsRef = React.createRef()
    carrierStepRef = React.createRef()
    planStepRef = React.createRef()

    render() {
        const {
            showSteps,
            showCarrierAndPlanHeader,
        } = this.props;

        if (!showSteps && !showCarrierAndPlanHeader) {
            return null;
        }

        return (
            <Wrapper
                ref={this.stepsRef}
                data-test="insurance-picker-header"
            >
                {this.renderCarrier()}
                {this.renderSteps()}
            </Wrapper>
        );
    }

    componentDidMount() {
        const {
            tabChange,
            markTabChange,
        } = this.props;

        if (!tabChange || !this.bcbsStepRef.current) {
            return;
        }

        const totalWidth = this.stepsRef.current.offsetWidth + 10;
        const carrierWidth = this.carrierStepRef.current.offsetWidth;
        const planWidth = this.planStepRef.current.offsetWidth;
        const bcbsWidth = this.bcbsStepRef.current.offsetWidth;
        const totalTabWidth = carrierWidth + planWidth + bcbsWidth;

        if (totalWidth > totalTabWidth) {
            return;
        }

        markTabChange();
    }

    renderSteps() {
        const { showSteps } = this.props;

        if (showSteps) {
            return (
                <StepsWrapper data-test="insurance-picker-header-steps">
                    <StepsContent>
                        {this.renderCarrierStep()}
                        {this.renderBcbsStep()}
                        {this.renderPlanStep()}
                    </StepsContent>
                </StepsWrapper>
            );
        }
    }

    renderCarrierStep() {
        const {
            tab,
            carrier,
            canSeeBcbs,
        } = this.props;
        const selected = tab === TAB.INSURANCE;

        return (
            <Step
                ref={this.carrierStepRef}
                canSeeBcbs={canSeeBcbs}
                data-test="carrier-step"
                onClick={() => this.props.viewInsuranceTab()}
                selected={selected}
            >
                <FormattedMessage
                    defaultMessage="Step 1: Choose carrier"
                    id="Components.InsurancePicker.Header.Step1"
                />
                {this.renderIcon(carrier)}
            </Step>
        );
    }

    renderBcbsStep() {
        const {
            canSeeBcbs,
            tab,
            planDisabled,
        } = this.props;
        const selected = tab === TAB.BCBS;

        if (!canSeeBcbs) {
            return;
        }

        return (
            <Step
                ref={this.bcbsStepRef}
                canSeeBcbs={canSeeBcbs}
                data-test="bcbs-step"
                onClick={() => this.props.viewBcbsTab()}
                selected={selected}
            >
                <FormattedMessage
                    defaultMessage="Step 2: Choose BCBS"
                    id="Components.InsurancePicker.Header.BcbsStep"
                />
                {this.renderIcon(!planDisabled)}
            </Step>
        );
    }

    renderPlanStep() {
        const {
            tab,
            planDisabled,
            canSeeBcbs,
            plan,
        } = this.props;

        const message = canSeeBcbs ? 'Step 3: Choose plan' : 'Step 2: Choose plan';
        const step = canSeeBcbs ? 'Components.InsurancePicker.Header.PlanStep3'
            : 'Components.InsurancePicker.Header.PlanStep2';
        const selected = planDisabled ? false : tab === TAB.PLAN;

        return (
            <Step
                ref={this.planStepRef}
                canSeeBcbs={canSeeBcbs}
                data-test="plan-step"
                disabled={planDisabled}
                onClick={() => this.clickPlanTab()}
                selected={selected}
            >
                <FormattedMessage
                    defaultMessage={message}
                    id={step}
                />
                {this.renderIcon(plan)}
            </Step>
        );
    }

    renderCarrier() {
        const {
            carrier,
            showCarrierAndPlanHeader,
        } = this.props;

        if (!carrier || !showCarrierAndPlanHeader) {
            return;
        }

        let { name } = carrier;

        if (carrier.id === CARRIER.BLUE_CROSS_BLUE_SHIELD) {
            name += ' (BCBS)';
        }

        return (
            <Carrier data-test="selected-carrier-name">
                {name}
            </Carrier>
        );
    }

    clickPlanTab() {
        const {
            planDisabled,
        } = this.props;

        if (planDisabled) {
            return;
        }

        this.props.viewPlanTab();
    }

    renderIcon(shouldRender) {
        if (!shouldRender) {
            return;
        }

        return (
            <StyledCheck
                data-test="selected-icon"
                height={9.7058823532}
            />
        );
    }
}

const Wrapper = styled.div`
    padding: 16px ${NON_INPUT_X_AFFORDANCE_MOBILE}px 8px;
    position: relative;

    ${breakpoint.small} {
        padding-left: 25px;
        padding-right: 25px;
    }
`;

const Step = styled.div`
    border-bottom: 2px solid ${p => p.selected ? navy : grey25};
    box-sizing: border-box;
    color: ${p => p.disabled ? grey75 : navy};
    cursor: ${p => p.disabled ? 'cursor' : 'pointer'};
    display: inline-block;
    font: 13px/18px ${FontFamily.SharpSansSemibold};
    min-width: 160px;
    padding: 0 0 8px;
    width: ${p => p.canSeeBcbs ? '33%' : '50%'};
`;

const Carrier = styled.div`
    font: 18px/24px ${FontFamily.SharpSansSemibold};
    color: ${navy};
    font-size: 18px;
    line-height: 24px;
    width: 100%;
    padding: 0 ${NON_INPUT_X_AFFORDANCE_MOBILE}px 8px 0;

    ${breakpoint('small')} {
        padding: 4px ${NON_INPUT_X_AFFORDANCE_DESKTOP}px 8px 0;
    }
`;

const StyledCheck = styled(Check)`
    margin-left: 5px;
    position: relative;
    top: 1px;
`;

const StepsWrapper = styled.div`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
        width: 0 !important;  // remove scrollbar space
        background: transparent;  // optional: just make scrollbar invisible
        display: none;
    }

    ${breakpoint.small} {
        overflow-x: inherit;
        width: 100%;
    }
`;

const StepsContent = styled.div`
    white-space: nowrap;
`;

export const HeaderView = injectIntl(BaseHeaderView);
